import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Paper, IconButton, Grid, Tooltip, Chip } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import useConfirmDeleteDialog from '../../hooks/useConfirmDeleteDialog';
import useCheckPermission from '../../hooks/useCheckPermission';
import DataList from '../../components/DataList';
import tenantGroupService from './tenantGroupService';

const TenantGroupPage = () => {
  const navigate = useNavigate();
  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();
  const [rows, setRows] = useState<any[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();
  const { canPlatform } = useCheckPermission();
  const canManage = canPlatform(['ManageTenantGroups', 'ManageTenants']);

  const load = () => {
    dispatch({ type: 'SHOW_QUERY' });
    tenantGroupService
      .list()
      .then((response) => {
        if (!response.hasError) setRows(response.records);
        else
          enqueueSnackbar(response.errorMessages?.join(',') || 'Hiba', {
            variant: 'error',
          });
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  useEffect(() => {
    load();
  }, []);

  const getActions = (params: GridRowParams) => {
    const actions: any[] = [
      <GridActionsCellItem
        key="edit"
        color="primary"
        icon={
          <Tooltip title={canManage ? 'Szerkesztés' : 'Megtekintés'}>
            <EditIcon />
          </Tooltip>
        }
        label={canManage ? 'Szerkesztés' : 'Megtekintés'}
        onClick={() => navigate(`/tenant-groups/edit/${params.row.id}`)}
      />,
    ];
    if (canManage) {
      actions.push(
        <GridActionsCellItem
          key="delete"
          color="primary"
          icon={
            <Tooltip title="Törlés">
              <DeleteIcon color="error" />
            </Tooltip>
          }
          label="Törlés"
          onClick={() =>
            setParams({
              open: true,
              name: params.row.name,
              onConfirm: async () => {
                await tenantGroupService.delete(params.row.id);
                load();
              },
            })
          }
        />
      );
    }
    return actions;
  };

  const columns = [
    { field: 'name', headerName: 'Név', flex: 1, minWidth: 160 },
    { field: 'slug', headerName: 'Slug', flex: 1, minWidth: 120 },
    { field: 'memberCount', headerName: 'Tagok', width: 90 },
    {
      field: 'isActive',
      headerName: 'Aktív',
      width: 100,
      renderCell: (p: any) =>
        p.value ? (
          <Chip size="small" color="success" label="Igen" />
        ) : (
          <Chip size="small" label="Nem" />
        ),
    },
    {
      field: 'actions',
      type: 'actions' as const,
      width: 100,
      getActions,
    },
  ];

  return (
    <Paper>
      <Grid container sx={{ p: 3 }}>
        <Grid size={12}>
          <h2>Bérlőcsoportok</h2>
        </Grid>
        {canManage && (
          <Grid container size={12} sx={{ p: 2, justifyContent: 'end' }}>
            <Grid>
              <Tooltip title="Új csoport">
                <IconButton
                  component={RouterLink}
                  to="/tenant-groups/create"
                  color="primary"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        )}
        <Grid size={12}>
          <DataList
            rows={rows}
            getRowId={(row: any) => row.id}
            localStorageKey="TenantGroupPage"
            columns={columns}
          />
        </Grid>
        <ConfirmDeleteDialog />
      </Grid>
    </Paper>
  );
};

export default TenantGroupPage;
